import _ from 'lodash';
import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type Item, createItem, shouldDehydrateItem } from '@/trendData/itemStore.utilities';
import { baseItemInitialState } from '@/trendData/itemStoreBase.constants';
import { createBaseItemActions } from '@/trendData/itemStoreBase.utilities';
import { ITEM_TYPES } from '@/trendData/trendData.constants';
import { type ActionsFactory, type StoreInstance, createStore, createStoreInstance } from '@seeqdev/utils';
import type { BaseItemActions, BaseItemState } from '@/trendData/itemStoreBase.types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ScalingColumnDehydratedItem {
  id: string;
  name: string;
  tableName?: string;
  tableId?: string;
  sampleItemId?: string;
  sampleItemName?: string;
}

interface ScalingColumnProps {
  tableName?: string;
  tableId?: string;
  sampleItemId?: string;
  sampleItemName?: string;
  [key: string]: unknown;
}

export type TrendScalingColumnState = BaseItemState;

export type TrendScalingColumnDehydratedState = {
  items: ScalingColumnDehydratedItem[];
};

export interface TrendScalingColumnActions extends BaseItemActions {
  addScalingColumn(payload: { id: string; name: string; props: ScalingColumnProps }): void;
  reset(): void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function createColumn(id: string, name: string, props: ScalingColumnProps): Item {
  return createItem(id, name, ITEM_TYPES.SCALING_COLUMN, {
    tableName: props.tableName,
    tableId: props.tableId,
    sampleItemId: props.sampleItemId,
    sampleItemName: props.sampleItemName,
  });
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

export const initialState: TrendScalingColumnState = { ...baseItemInitialState };

// ---------------------------------------------------------------------------
// Dehydrate / rehydrate
// ---------------------------------------------------------------------------

export const dehydrateTrendScalingColumnState = (
  state: TrendScalingColumnState,
): TrendScalingColumnDehydratedState => ({
  items: state.items
    .filter(shouldDehydrateItem)
    .map(
      (item) =>
        _.pick(item, [
          'id',
          'name',
          'tableName',
          'tableId',
          'sampleItemId',
          'sampleItemName',
        ]) as ScalingColumnDehydratedItem,
    ),
});

export function rehydrateTrendScalingColumnState(
  dehydratedState: Partial<TrendScalingColumnDehydratedState>,
  setState: (state: { items: Item[] }) => void,
): void {
  const items = (dehydratedState.items ?? []).map((item) =>
    createColumn(item.id, item.name, _.omit(item, ['id', 'name'])),
  );
  setState({ items });
}

// ---------------------------------------------------------------------------
// Actions factory
// ---------------------------------------------------------------------------

const STORE_NAME = 'TrendScalingColumn';

const scalingColumnActionsFactory: ActionsFactory<TrendScalingColumnState, TrendScalingColumnActions> = (
  set,
  _get,
  resetState,
) => ({
  ...createBaseItemActions<TrendScalingColumnState>(set),

  addScalingColumn: (payload) =>
    set((draft) => {
      draft.items.push(createColumn(payload.id, payload.name, payload.props));
    }),

  reset: resetState,
});

// ---------------------------------------------------------------------------
// Store creation
// ---------------------------------------------------------------------------

const [TrendScalingColumnStoreProvider, useTrendScalingColumnStoreInstance, useTrendScalingColumnSelector] =
  createStore(STORE_NAME, initialState, scalingColumnActionsFactory);

export { TrendScalingColumnStoreProvider };

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const useTrendScalingColumnItems = () => useTrendScalingColumnSelector((state) => state.items);

export const useTrendScalingColumnActions = () => useTrendScalingColumnStoreInstance().actions;

export const usePersistTrendScalingColumnStore = () => {
  const register = useRegisterPersistedStore();
  const store = useTrendScalingColumnStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqTrendScalingColumnStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateTrendScalingColumnState,
      rehydrate: (dehydratedState: Partial<TrendScalingColumnDehydratedState>) =>
        rehydrateTrendScalingColumnState(dehydratedState, (partial) => store.setState(partial)),
    });
  }, [register, store]);
};

// ---------------------------------------------------------------------------
// Legacy singleton bridge
// ---------------------------------------------------------------------------

export type LegacyTrendScalingColumnStore = StoreInstance<TrendScalingColumnState, TrendScalingColumnActions>;

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. Read state via `getState()` (e.g. `findItem(store.getState().items, id)`)
 * and mutate via `store.actions.*`. New code should use the hooks above and the Provider.
 */
export function legacyCreateTrendScalingColumnStore(
  initialStateOverrides?: Partial<TrendScalingColumnState>,
): LegacyTrendScalingColumnStore {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    scalingColumnActionsFactory,
    { storeName: STORE_NAME },
  );
}
